import type { Node, Edge } from '@xyflow/react';
import type { WorkflowDefinitionResponse } from '@erp/rpc';
import { v4 as uuidv4 } from 'uuid';

interface MMGraphSocket {
  id: string;
  ioType: number;
  socketType?: string;
}

interface NodeDataType extends Record<string, unknown> {
  label: string;
  inputs: {
    [key: string]: MMGraphSocket;
  };
  outputs: {
    [key: string]: MMGraphSocket;
  };
  stepType?: string;
  config?: Record<string, unknown>;
  emits?: string[];
  _stepTypeData?: {
    type: string;
    name: string;
    description: string;
    category: string;
    inputSockets: Array<{
      id: string;
      name: string;
      type: string;
      required?: boolean;
      description?: string;
    }>;
    outputSockets: Array<{
      id: string;
      name: string;
      type: string;
      description?: string;
    }>;
  };
}

type StepDefinition = WorkflowDefinitionResponse['steps'][0];
type EdgeDefinition = WorkflowDefinitionResponse['edges'][0];
type SocketType = StepDefinition['sockets']['inputs'][0]['type'];

/**
 * Convert React Flow nodes and edges back to workflow definition format
 */
export function graphToDefinition(
  nodes: Node[],
  edges: Edge[],
  originalDefinition: WorkflowDefinitionResponse,
): {
  steps: StepDefinition[];
  edges: EdgeDefinition[];
} {
  // Create a map of original steps for reference
  const originalStepMap = new Map<string, StepDefinition>();
  originalDefinition.steps.forEach((step) => {
    originalStepMap.set(step.id, step);
  });

  // Convert nodes to steps
  const steps: StepDefinition[] = nodes.map((node) => {
    const data = node.data as NodeDataType;
    const originalStep = originalStepMap.get(node.id);

    // If this is an existing node, use original data where possible
    // but allow config to be updated from node data
    if (originalStep) {
      const updatedStep: StepDefinition = {
        ...originalStep,
        // Use config from node data if available, otherwise keep original
        config: data.config ?? originalStep.config,
        position: {
          x: Math.round(node.position.x),
          y: Math.round(node.position.y),
        },
      };

      // For steps with config-dependent sockets (currently event_trigger),
      // persist the current node sockets so edges validate server-side.
      if (updatedStep.type === 'event_trigger') {
        updatedStep.sockets = {
          ...updatedStep.sockets,
          outputs: Object.entries(data.outputs).map(([name, socket]) => ({
            id: socket.id,
            name,
            type: (socket.socketType || 'any') as SocketType,
            required: true,
            description: '',
          })),
        };
      }

      return updatedStep;
    }

    // This is a new node from the node library
    const stepTypeData = data._stepTypeData;
    if (stepTypeData) {
      return {
        id: node.id,
        type: stepTypeData.type,
        name: data.label || stepTypeData.name,
        category: stepTypeData.category as StepDefinition['category'],
        config: data.config ?? {},
        sockets: {
          // Use original socket.id from registry - orchestrator validates against these
          inputs: stepTypeData.inputSockets.map((socket) => ({
            id: socket.id,
            name: socket.name,
            type: (socket.type || 'any') as SocketType,
            required: socket.required ?? true,
            description: socket.description,
          })),
          outputs: stepTypeData.outputSockets.map((socket) => ({
            id: socket.id,
            name: socket.name,
            type: (socket.type || 'any') as SocketType,
            required: true,
            description: socket.description,
          })),
        },
        position: {
          x: Math.round(node.position.x),
          y: Math.round(node.position.y),
        },
      };
    }

    // Fallback for nodes without type data - shouldn't normally happen
    return {
      id: node.id,
      type: data.stepType || 'unknown',
      name: data.label || 'Unknown Step',
      category: 'Utility' as StepDefinition['category'],
      config: data.config ?? {},
      sockets: {
        inputs: Object.entries(data.inputs).map(([name, socket]) => ({
          id: socket.id,
          name,
          type: 'any' as SocketType,
          required: true,
        })),
        outputs: Object.entries(data.outputs).map(([name, socket]) => ({
          id: socket.id,
          name,
          type: 'any' as SocketType,
          required: true,
        })),
      },
      position: {
        x: Math.round(node.position.x),
        y: Math.round(node.position.y),
      },
    };
  });

  // Create a map of node IDs to their sockets for edge mapping
  const nodeSocketMap = new Map<
    string,
    { inputs: Map<string, string>; outputs: Map<string, string> }
  >();
  nodes.forEach((node) => {
    const data = node.data as NodeDataType;
    const inputs = new Map<string, string>();
    const outputs = new Map<string, string>();

    Object.values(data.inputs).forEach((socket) => {
      inputs.set(socket.id, socket.id);
    });
    Object.values(data.outputs).forEach((socket) => {
      outputs.set(socket.id, socket.id);
    });

    nodeSocketMap.set(node.id, { inputs, outputs });
  });

  // UUID regex for validation
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  // Convert edges to definition format
  const definitionEdges: EdgeDefinition[] = edges
    .map((edge) => {
      // Validate that source and target nodes exist
      if (
        !nodes.find((n) => n.id === edge.source) ||
        !nodes.find((n) => n.id === edge.target)
      ) {
        return null;
      }

      // Use existing ID if it's a valid UUID, otherwise generate new one
      const edgeId = uuidRegex.test(edge.id) ? edge.id : uuidv4();

      return {
        id: edgeId,
        sourceStepId: edge.source,
        sourceSocketId: edge.sourceHandle || '',
        targetStepId: edge.target,
        targetSocketId: edge.targetHandle || '',
      };
    })
    .filter((edge): edge is EdgeDefinition => edge !== null);

  return { steps, edges: definitionEdges };
}
